import { Fragment, useState } from 'react'
import { Plane, Building2, MapPin, Filter, BellRing, CalendarRange, FileText, FileSpreadsheet } from 'lucide-react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'
import { SeletorMultiplo } from './RelatorioConsumoArla'

// Controle de férias: período aquisitivo/concessivo, saldo, programação e alerta de vencimento (calculado da admissão + gozos lançados)

type StatusFunc = 'VENCIDA' | 'A_VENCER' | 'NO_PRAZO' | 'PROGRAMADA' | 'EM_DIA'
type StatusPeriodo = StatusFunc | 'EM_AQUISICAO' | 'QUITADA'
interface Gozo { ini: string; fim: string; dias: number; programado: boolean }
interface Periodo { ini: string; fim: string; limite: string; direito: number; gozados: number; programados: number; vendidos: number; saldo: number; diasLimite: number; status: StatusPeriodo; gozos: Gozo[] }
interface Func { motcod: string; nome: string; cargo: string; admissao: string; status: StatusFunc; vencidas: number; critico: Periodo | null; periodos: Periodo[] }

const ROTULO: Record<StatusPeriodo, string> = {
  VENCIDA: 'Vencida', A_VENCER: 'A vencer', NO_PRAZO: 'No prazo', PROGRAMADA: 'Programada', EM_DIA: 'Em dia', EM_AQUISICAO: 'Em aquisição', QUITADA: 'Quitada',
}
const COR: Record<StatusPeriodo, { fg: string; bg: string }> = {
  VENCIDA: { fg: '#991b1b', bg: '#fee2e2' }, A_VENCER: { fg: '#92400e', bg: '#fef3c7' }, NO_PRAZO: { fg: '#1f2937', bg: '#f1f5f9' },
  PROGRAMADA: { fg: '#1e40af', bg: '#dbeafe' }, EM_DIA: { fg: '#166534', bg: '#dcfce7' }, EM_AQUISICAO: { fg: '#475569', bg: '#f1f5f9' }, QUITADA: { fg: '#166534', bg: '#dcfce7' },
}
const dataBr = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

function Selo({ s }: { s: StatusPeriodo }) {
  return <span style={{ padding: '0.12rem 0.55rem', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, color: COR[s].fg, background: COR[s].bg, whiteSpace: 'nowrap' }}>{ROTULO[s]}</span>
}

const prazo = (p: Periodo) => {
  if (p.status === 'QUITADA' || p.status === 'EM_AQUISICAO') return ''
  return p.diasLimite < 0 ? `vencido há ${-p.diasLimite} d` : `faltam ${p.diasLimite} d`
}

export default function ControleFerias() {
  const { empresas, empresaCod } = useEmpresa()
  const hoje = new Date()
  const [empresasSel, setEmpresasSel] = useState<string[]>([])
  const [filial, setFilial] = useState('')
  const [status, setStatus] = useState('')
  const [avisoDias, setAvisoDias] = useState(90)
  const [desde, setDesde] = useState(isoLocal(new Date(hoje.getFullYear() - 3, hoje.getMonth(), hoje.getDate())))
  const [res, setRes] = useState<Func[] | null>(null)
  const [aberto, setAberto] = useState('')
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  const params = () => ({ empresas: empresasSel.join(','), filial, status, avisoDias, desde, empresa: empresaCod ?? 1 })

  async function gerar() {
    setErro('')
    setCarregando(true)
    try {
      const r = await api.get('/funcionarios/relatorio/controle-ferias', { params: params() })
      setRes(r.data)
      setAberto('')
    } catch (e: any) {
      setRes(null)
      setErro(e?.response?.data?.error || 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro('')
    setGerando(formato)
    try {
      const r = await api.get('/funcionarios/relatorio/controle-ferias', { params: { ...params(), formato }, responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') {
        window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = 'controle-ferias.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch (e: any) {
      let msg = formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.'
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).error || msg } catch {}
      setErro(msg)
    } finally {
      setGerando('')
    }
  }

  const th: React.CSSProperties = { padding: '0.5rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', textAlign: 'left', whiteSpace: 'nowrap' }
  const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
  const cont = (s: StatusFunc) => res?.filter(l => l.status === s).length ?? 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <Plane size={18} color="#fff" />
            </span>
            <div>
              <h2>Controle de Férias</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Período aquisitivo e concessivo, saldo, programação e férias vencendo</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Estabelecimento" icon={Building2}>
            <SeletorMultiplo opcoes={empresas.map(e => ({ cod: String(e.empcod), des: `${e.empcod} — ${e.empfan?.trim() || e.empnom?.trim()}` }))}
              selecionados={empresasSel} onChange={setEmpresasSel} placeholderBusca="Buscar empresa..." />
          </Linha>

          <Linha label="Filial" icon={MapPin}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input value={filial} onChange={e => setFilial(e.target.value)} maxLength={2} style={{ maxWidth: 70 }} />
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todas</span>
            </div>
          </Linha>

          <Linha label="Situação" icon={Filter}>
            <select value={status} onChange={e => setStatus(e.target.value)} style={{ maxWidth: 200 }}>
              <option value="">Todas</option>
              <option value="VENCIDA">Vencidas</option>
              <option value="A_VENCER">A vencer</option>
              <option value="NO_PRAZO">No prazo</option>
              <option value="PROGRAMADA">Programadas</option>
              <option value="EM_DIA">Em dia</option>
            </select>
          </Linha>

          <Linha label="Alertar com" icon={BellRing}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input type="number" min={1} value={avisoDias} onChange={e => setAvisoDias(Number(e.target.value))} style={{ maxWidth: 80 }} />
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>dias para o fim do prazo concessivo</span>
            </div>
          </Linha>

          <Linha label="Períodos desde" icon={CalendarRange}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={{ maxWidth: 170 }} />
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>ignora períodos com prazo anterior a esta data</span>
            </div>
          </Linha>

          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
              {erro}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {(['VENCIDA', 'A_VENCER', 'NO_PRAZO', 'PROGRAMADA', 'EM_DIA'] as StatusFunc[]).map(s => (
                <span key={s} style={{ padding: '0.3rem 0.8rem', borderRadius: 8, fontSize: '0.78rem', fontWeight: 700, color: COR[s].fg, background: COR[s].bg }}>
                  {ROTULO[s]}: {cont(s)}
                </span>
              ))}
            </div>

            {res.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum funcionário encontrado.</div>}

            {res.length > 0 && (
              <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Código</th><th style={th}>Funcionário</th><th style={th}>Cargo/Função</th><th style={th}>Admissão</th>
                      <th style={th}>Período Aquisitivo</th><th style={th}>Limite Concessivo</th>
                      <th style={{ ...th, textAlign: 'right' }}>Gozados</th><th style={{ ...th, textAlign: 'right' }}>Progr.</th><th style={{ ...th, textAlign: 'right' }}>Vend.</th><th style={{ ...th, textAlign: 'right' }}>Saldo</th>
                      <th style={th}>Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {res.map((l, i) => {
                      const c = l.critico
                      const aberta = aberto === l.motcod
                      return (
                        <Fragment key={l.motcod}>
                          <tr onClick={() => setAberto(aberta ? '' : l.motcod)} style={{ background: i % 2 ? '#f5f9fd' : '#fff', cursor: 'pointer' }}>
                            <td style={{ ...td, fontWeight: 600 }}>{l.motcod}</td>
                            <td style={{ ...td, fontWeight: 600 }}>{aberta ? '▾' : '▸'} {l.nome}</td>
                            <td style={td}>{l.cargo}</td>
                            <td style={td}>{dataBr(l.admissao)}</td>
                            <td style={td}>{c ? `${dataBr(c.ini)} a ${dataBr(c.fim)}` : ''}</td>
                            <td style={td}>{c ? <>{dataBr(c.limite)} <span style={{ color: '#6b7280', fontSize: '0.72rem' }}>{prazo(c)}</span></> : ''}</td>
                            <td style={num}>{c?.gozados}</td>
                            <td style={num}>{c?.programados}</td>
                            <td style={num}>{c?.vendidos}</td>
                            <td style={{ ...num, fontWeight: 700 }}>{c?.saldo}</td>
                            <td style={td}><Selo s={l.status} />{l.vencidas > 1 && <span style={{ marginLeft: 6, fontSize: '0.7rem', color: '#991b1b', fontWeight: 700 }}>{l.vencidas} períodos</span>}</td>
                          </tr>
                          {aberta && (
                            <tr>
                              <td colSpan={11} style={{ padding: '0.6rem 1rem 0.9rem', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                  <thead>
                                    <tr>
                                      {['Período Aquisitivo', 'Limite Concessivo', 'Direito', 'Gozados', 'Progr.', 'Vendidos', 'Saldo', 'Situação', 'Gozos'].map(h => (
                                        <th key={h} style={{ ...td, fontSize: '0.68rem', fontWeight: 800, color: '#1b3f7b', textTransform: 'uppercase', borderBottom: '1px solid #cbd5e1' }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {[...l.periodos].reverse().map(p => (
                                      <tr key={p.ini}>
                                        <td style={td}>{dataBr(p.ini)} a {dataBr(p.fim)}</td>
                                        <td style={td}>{dataBr(p.limite)} <span style={{ color: '#6b7280', fontSize: '0.72rem' }}>{prazo(p)}</span></td>
                                        <td style={num}>{p.direito}</td><td style={num}>{p.gozados}</td><td style={num}>{p.programados}</td><td style={num}>{p.vendidos}</td>
                                        <td style={{ ...num, fontWeight: 700 }}>{p.saldo}</td>
                                        <td style={td}><Selo s={p.status} /></td>
                                        <td style={{ ...td, whiteSpace: 'normal', fontSize: '0.76rem', color: '#475569' }}>
                                          {p.gozos.map(g => `${dataBr(g.ini)}${g.fim !== g.ini ? ` a ${dataBr(g.fim)}` : ''} (${g.dias} d${g.programado ? ', programado' : ''})`).join(' · ')}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ borderRadius: 12, padding: '0.8rem 1.1rem', background: 'linear-gradient(135deg, #0f2a52, #1b3f7b)', color: '#fff', fontWeight: 800, letterSpacing: '0.02em' }}>
              TOTAL DE FUNCIONÁRIOS: {res.length}
            </div>
            <div style={{ fontSize: '0.72rem', color: '#6b7280' }}>
              Direito de 30 dias por período aquisitivo (12 meses da admissão); o concessivo são os 12 meses seguintes. Os gozos lançados no cadastro (botão Férias)
              abatem do período mais antigo com saldo; gozo com início futuro conta como programado. Os dias vendidos (abono pecuniário, também no botão Férias) saem
              do saldo do período em que foram lançados. Clique numa linha para ver todos os períodos.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
